import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { tagButtonStyle } from "@/components/FinancialShared/tableStyles";
import {
  IParcelaDetalhadaRetorno,
  IRelatorioQuitacaoRetorno,
} from "@/models/consignado/proposal-billings.model";
import { gerarRelatorioQuitacao } from "@/services/consignado/proposta-cobranca.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { formatDateTimeWithAt } from "@/utils/dateFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { hojeYYYYMMDD } from "./dateYYYYMMDD";
import { formLabelProps, modalHeaderProps } from "./modalStyles";
import RelatorioGeradoCard from "./RelatorioGeradoCard";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";

// Janela de configuração do "Relatório de quitação" (TAR-ADM-cobranca-044,
// rota REAL da TAR-043, backend his-adm-cobranca-020 `65a9ba13`): o operador
// escolhe as parcelas, a data de cálculo e se permite descapitalização →
// POST .../relatorio-quitacao → abre o PDF devolvido.
// O front não soma nem deriva nada: lista as parcelas e manda o pedido; o
// total a quitar vem pronto do backend ([[no-front-calculations]]).
// Erro da BMP (422/503) chega com `message` pronta pro operador → toast.

type Props = {
  isOpen: boolean;
  onClose: () => void;
  numeroCcb: string;
  parcelas: IParcelaDetalhadaRetorno[];
};

const renderDataOuTraco = (data?: string | null) => (data ? formatDateWithNoHours(data) : "—");

export default function RelatorioQuitacaoModal({ isOpen, onClose, numeroCcb, parcelas }: Props) {
  const toastErro = useApiErrorToast();
  const [selecionadas, setSelecionadas] = useState<number[]>([]);
  const [dataCalculo, setDataCalculo] = useState<string>(hojeYYYYMMDD);
  const [permiteDescapitalizacao, setPermiteDescapitalizacao] = useState(true);

  const parcelasSelecionaveis = parcelas.filter(
    (parcela): parcela is IParcelaDetalhadaRetorno & { numero_parcela: number } =>
      parcela.numero_parcela != null
  );
  const numerosDisponiveis = parcelasSelecionaveis.map((parcela) => parcela.numero_parcela);
  const todasSelecionadas =
    numerosDisponiveis.length > 0 && selecionadas.length === numerosDisponiveis.length;
  const algumaSelecionada = selecionadas.length > 0 && !todasSelecionadas;

  const toggleParcela = (numero: number) =>
    setSelecionadas((prev) =>
      prev.includes(numero) ? prev.filter((n) => n !== numero) : [...prev, numero]
    );

  const toggleTodas = () => setSelecionadas(todasSelecionadas ? [] : numerosDisponiveis);

  const gerar = useMutation({
    mutationFn: () =>
      gerarRelatorioQuitacao(numeroCcb, {
        parcelas: [...selecionadas].sort((a, b) => a - b),
        data_calculo: dataCalculo,
        permite_descapitalizacao: permiteDescapitalizacao,
      }),
    onSuccess(relatorio) {
      window.open(relatorio.url, "_blank", "noopener");
    },
    onError: toastErro("Erro ao gerar relatório de quitação"),
  });

  const handleClose = () => {
    setSelecionadas([]);
    setDataCalculo(hojeYYYYMMDD());
    setPermiteDescapitalizacao(true);
    gerar.reset();
    onClose();
  };

  const relatorio: IRelatorioQuitacaoRetorno | undefined = gerar.data;

  const motivoDesabilitado =
    selecionadas.length === 0
      ? "Selecione ao menos uma parcela."
      : !dataCalculo
        ? "Informe a data de cálculo."
        : "";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="lg" boxShadow="2xl">
        <ModalHeader {...modalHeaderProps}>Relatório de Quitação</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={6}>
          <Flex direction="column" gap={5} fontFamily="Lexend Deca">
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="sm" fontWeight="500" color="gray.700">
                  Parcelas da CCB {numeroCcb}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {selecionadas.length} de {numerosDisponiveis.length} selecionada
                  {selecionadas.length === 1 ? "" : "s"}
                </Text>
              </Flex>
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="md">
                <Box px={3} py={2} borderBottomWidth="1px" borderBottomColor="gray.200" bg="gray.50">
                  <Checkbox
                    colorScheme="green"
                    isChecked={todasSelecionadas}
                    isIndeterminate={algumaSelecionada}
                    isDisabled={numerosDisponiveis.length === 0}
                    onChange={toggleTodas}
                    fontSize="sm"
                    sx={{ ".chakra-checkbox__control": { borderColor: "gray.500" } }}
                  >
                    <Text fontSize="sm" fontWeight="500">
                      Selecionar todas
                    </Text>
                  </Checkbox>
                </Box>
                <Box maxH="240px" overflowY="auto" px={3} py={1}>
                  {parcelasSelecionaveis.length === 0 ? (
                    <Text fontSize="xs" color="gray.500" py={2}>
                      Nenhuma parcela disponível para esta CCB.
                    </Text>
                  ) : (
                    parcelasSelecionaveis.map((parcela) => (
                      <Flex key={parcela.id} py={1.5} alignItems="center">
                        <Checkbox
                          colorScheme="green"
                          isChecked={selecionadas.includes(parcela.numero_parcela)}
                          onChange={() => toggleParcela(parcela.numero_parcela)}
                          sx={{ ".chakra-checkbox__control": { borderColor: "gray.500" } }}
                        >
                          <Text fontSize="sm">
                            Parcela {parcela.numero_parcela}
                            <Text as="span" color="gray.500">
                              {" "}
                              · venc. {renderDataOuTraco(parcela.vencimento)} ·{" "}
                              {BrlCurrencyFormatter(parcela.valor)}
                            </Text>
                          </Text>
                        </Checkbox>
                      </Flex>
                    ))
                  )}
                </Box>
              </Box>
            </Box>

            <FormControl isRequired>
              <FormLabel {...formLabelProps}>Data de cálculo</FormLabel>
              <Input
                type="date"
                size="sm"
                maxW="200px"
                value={dataCalculo}
                onChange={(e) => setDataCalculo(e.target.value)}
              />
              <FormHelperText fontSize="xs">
                O valor atualizado é calculado pela bancarizadora para esta data.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Checkbox
                colorScheme="green"
                isChecked={permiteDescapitalizacao}
                onChange={(e) => setPermiteDescapitalizacao(e.target.checked)}
                sx={{ ".chakra-checkbox__control": { borderColor: "gray.500" } }}
              >
                <Text fontSize="sm" fontWeight="500">
                  Permitir descapitalização
                </Text>
              </Checkbox>
              <FormHelperText fontSize="xs" mt={1}>
                Aplica descapitalização e encargos no cálculo da bancarizadora.
              </FormHelperText>
            </FormControl>

            {relatorio && (
              <RelatorioGeradoCard nomeArquivo={relatorio.nome_arquivo} url={relatorio.url}>
                <Text color="gray.600" fontSize="xs">
                  {relatorio.quantidade_parcelas} parcela{relatorio.quantidade_parcelas === 1 ? "" : "s"}
                  {" · cálculo em "}
                  {formatDateWithNoHours(relatorio.data_calculo)}
                  {relatorio.permite_descapitalizacao ? " · com descapitalização" : " · sem descapitalização"}
                </Text>
                <Text fontWeight="600" color="green.700">
                  Total a quitar: {BrlCurrencyFormatter(relatorio.total_quitacao)}
                </Text>
                <Text color="gray.600" fontSize="xs">
                  Válido até {formatDateWithNoHours(relatorio.valido_ate)} · gerado em{" "}
                  {formatDateTimeWithAt(new Date(relatorio.gerado_em))}
                </Text>
              </RelatorioGeradoCard>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderTopColor="gray.200" pt={4}>
          <Tooltip label={motivoDesabilitado} isDisabled={!motivoDesabilitado} placement="top">
            <Button
              {...tagButtonStyle("blue")}
              fontFamily="Lexend Deca"
              isDisabled={Boolean(motivoDesabilitado)}
              isLoading={gerar.isPending}
              loadingText="Gerando..."
              onClick={() => gerar.mutate()}
            >
              Exportar relatório
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
